import { createFileRoute } from "@tanstack/react-router";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import type { ClientStudioDocHydrated } from "api-types/studioDocs/studioDocResponses";
import type { Doc } from "building-types/doc";
import { WEB_API_BASE_PATH } from "common/constants";
import { assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import docMarshal from "doc/docMarshal";
import { removeOrphanedInteriorPaletteOptionSet } from "doc/options/paletteOptionSet";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import PlanLibraryHeaderActions from "lib/components/builders/plan-library/header/PlanLibraryHeaderActions";
import PlanLibrarySubheader from "lib/components/builders/plan-library/header/PlanLibrarySubheader";
import PlanLibrary from "lib/components/builders/plan-library/PlanLibrary";
import PlanLibraryElevationsTab from "lib/components/builders/plan-library/tab/PlanLibraryElevationsTab";
import PlanLibraryHistoryTab from "lib/components/builders/plan-library/tab/PlanLibraryHistoryTab";
import PlanLibraryHomePlansTab from "lib/components/builders/plan-library/tab/PlanLibraryHomePlansTab";
import PlanLibraryLocationsTab from "lib/components/builders/plan-library/tab/PlanLibraryLocationsTab";
import PlanLibraryOptionSetsTab from "lib/components/builders/plan-library/tab/PlanLibraryOptionSetsTab";
import { useFetch } from "lib/fetcher";
import { masterPlanLibraryPath, useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { useEffect, useState } from "react";
import Capsule from "ui/components/Capsule";
import Loading from "ui/components/Loading";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import IconInfo from "ui/icons/status/IconInfo";
import withAuthentication from "../../../../../components/withAuthentication";
import styles from "../index.module.scss";

function PlanLibaryMasterPlan() {
  const { builderLoading, builder, hasPermission, enabledFeatures } = useUserContext();
  const visibleTabs: string[] = ["option_sets", "elevations", "home_plans"];
  if (hasPermission("VIEW_COMMUNITIES")) {
    visibleTabs.push("locations");
  }
  if (enabledFeatures?.optionCatalogHistory) {
    visibleTabs.push("history");
  }
  const { tab, menuItems } = useTabbedMenu(visibleTabs);
  const { builderId, masterPlanId } = useAppRouterParams();

  const [fetchResult, setFetchResult] = useState<{
    publishedDoc: Doc;
    masterPlan: ClientMasterPlanHydrated;
  } | null>(null);

  const { data: masterPlan } = useFetch<ClientMasterPlanHydrated>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}/hydrated`,
    [builderId, masterPlanId],
  );
  const { data: studioDoc } = useFetch<ClientStudioDocHydrated>(
    `${WEB_API_BASE_PATH}/studio-docs/${masterPlan?.studioDocId}?builderId=${builderId}&includePublishedDoc=true`,
    [builderId, masterPlan?.studioDocId],
  );

  useEffect(() => {
    if (masterPlan && studioDoc) {
      const publishedDoc = docMarshal(studioDoc.publishedDoc);
      setFetchResult({
        publishedDoc: {
          ...publishedDoc,
          optionSets: removeOrphanedInteriorPaletteOptionSet(publishedDoc.optionSets),
        },
        masterPlan: {
          ...masterPlan,
          doc: {
            ...masterPlan.doc,
            optionSets: removeOrphanedInteriorPaletteOptionSet(masterPlan.doc.optionSets),
          },
        },
      });
    }
  }, [masterPlan, studioDoc]);

  if (!builderLoading && fetchResult) {
    const { publishedDoc, masterPlan } = fetchResult;

    assertIsNotUndefined(builderId);
    assertIsNotUndefined(masterPlanId);
    assertIsNotUndefined(builder);
    assertIsNotNullOrUndefined(masterPlan.name);

    const isUsingStudioOverrides = enabledFeatures?.nextGenOptionsStudioOverrides === true;

    return (
      <PlanLibrary
        planId={masterPlanId}
        planType="master_plan"
        builder={builder}
        masterPlan={masterPlan}
        publishedDoc={publishedDoc}
        isReadOnly={!hasPermission("EDIT_MASTER_PLANS")}
      >
        <BuilderPortalLayout
          pageTitle={masterPlan.name}
          breadcrumbs={[
            { text: "Master Plans", href: masterPlanLibraryPath(builderId) },
            { text: masterPlan.name, href: "#" },
          ]}
          header={masterPlan.name}
          subheaderItems={[
            <PlanLibrarySubheader key="plan-library-subheader" />,
            ...(masterPlan.archivedAt
              ? [<Capsule key="archived" label="Archived" color="white" backgroundColor="stone" size="mini" />]
              : []),
          ]}
          headerActions={<PlanLibraryHeaderActions />}
          menuItems={menuItems}
          selected="plans"
          wide
        >
          <div className="builder-portal-layout-content">
            {tab === "option_sets" && (
              <>
                {isUsingStudioOverrides && (
                  <div className={styles.info}>
                    <IconInfo />
                    Studio doc overrides will take precedence on publish.
                  </div>
                )}
                <PlanLibraryOptionSetsTab />
              </>
            )}
            {tab === "elevations" && <PlanLibraryElevationsTab />}
            {tab === "home_plans" && <PlanLibraryHomePlansTab planType="master_plan" />}
            {tab === "locations" && <PlanLibraryLocationsTab />}
            {tab === "history" && <PlanLibraryHistoryTab />}
          </div>
        </BuilderPortalLayout>
      </PlanLibrary>
    );
  }

  return <Loading />;
}

export const Route = createFileRoute("/builders/$builder_id/master-plans/$master_plan_id/")({
  component: withAuthentication(PlanLibaryMasterPlan, { permissions: ["VIEW_MASTER_PLANS"] }),
});
